<template>
  <div class="template-menu">
    <div
      v-if="selectedFacetsFlat.length > 0"
      class="template-menu__selected-filters"
    >
      <button
        v-for="filter in selectedFacetsFlat"
        :key="`${filter.facet}-${filter.value}`"
        type="button"
        class="filter-chip"
        @click="removeFacet(filter.facet, filter.value)"
      >
        {{ formatFacetLabel(filter.facet) }}: {{ capitalizeWords(filter.value) }}
        <span class="fas fa-times" />
      </button>
    </div>

    <div
      v-if="loading"
      class="template-menu__loading"
    >
      <span class="fa-solid fa-spinner fa-spin" />
    </div>
    <div
      v-else-if="templates.length === 0"
      class="template-menu__empty"
    >
      No templates found.
    </div>
    <div
      v-else
      class="template-menu__list"
    >
      <div
        v-for="template in allowedTemplates"
        :key="template.id"
        class="template-item"
      >
        <!-- Normal template -->
        <button
          v-if="!template.upsell"
          type="button"
          class="template-card-button"
          :disabled="isDisabled(template)"
          :aria-label="`Use template ${template.title}`"
          @click="useTemplateClick(template)"
        >
          <div class="template-card__thumb">
            <img
              :src="template.posterUrl || template.thumbnailUrl"
              :alt="template.title"
              class="template-card__image"
            />
          </div>

          <span
            class="template-card__title"
            :title="template.title"
          >
            {{ template.title }}
          </span>
        </button>

        <!-- Upsell template -->
        <a
          v-else
          :href="template.upsellLink"
          target="_blank"
          rel="noopener noreferrer"
          class="template-card-button template-card-button--upsell"
        >
          <div class="template-card__thumb">
            <img
              :src="template.posterUrl || template.thumbnailUrl"
              :alt="template.title"
              class="template-card__image"
            />
          </div>

          <span
            class="template-card__title"
            :title="template.title"
          >
            {{ template.title }}
          </span>

          <span class="template-card__bolt fas fa-bolt" />
        </a>

        <button
          type="button"
          class="template-card__preview-btn btn btn-sm btn-icon btn-link"
          :title="`Preview ${template.title}`"
          @click.stop="openPreview(template)"
        >
          <span class="far fa-magnifying-glass" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { debounce, startCase } from 'lodash-es';
import Packs from '../../services/packs';
import toast from '../../utils/toasts';

import elementMeta from './elementData/index';

const graphicsUpsellLinks = {
  shoppable: 'https://landing.tagboard.com/shoppable',
  sportradar: 'https://landing.tagboard.com/sportradar',
};

export default {
  emits: ['facets-loaded', 'used', 'preview', 'remove-facet'],
  name: 'TemplateMenu',
  props: {
    search: {
      type: String,
      default: '',
    },
    selectedFacets: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      templates: [],
      loading: false,
      availableFacets: {},
      selectedFacetsFlat: [],
    };
  },
  computed: {
    ...mapGetters('graphicsTemplate', [
      'getElementCounts',
    ]),
    ...mapGetters([
      'hasSportradarElement',
      'hasShoppable',
    ]),

    allowedTemplates() {
      const list = this.templates.reduce((acc, result) => {
        let checked = result;

        // Check if template has shoppable or sportradar elements
        const hasShoppableElements = result.dataTypes?.includes('shoppable')
          || result.signals?.includes('shoppable');
        const hasSportradarElements = result.dataTypes?.includes('sportradar')
          || result.signals?.includes('sportradar');

        if (hasShoppableElements) {
          checked = this.checkAvailability(
            this.hasShoppable,
            result,
            graphicsUpsellLinks.shoppable,
          );
        } else if (hasSportradarElements) {
          checked = this.checkAvailability(
            this.hasSportradarElement,
            result,
            graphicsUpsellLinks.sportradar,
          );
        }

        if (checked) acc.push(checked);
        return acc;
      }, []);

      return list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },

    elementMetaLookup() {
      return Object.values(elementMeta).reduce(
        (acc, curr) => {
          curr.forEach((el) => {
            if (!acc[el.element]) {
              acc[el.element] = el;
            }
          });
          return acc;
        },
        {},
      );
    },

    groupedFacets() {
      return Object.entries(this.availableFacets)
        .filter(([, items]) => Array.isArray(items) && items.length > 0)
        .reduce((acc, [key, items]) => {
          acc[key] = items;
          return acc;
        }, {});
    },
  },
  watch: {
    search: {
      immediate: true,
      handler(newSearch) {
        this.fetchTemplates(newSearch);
      },
    },

    selectedFacets: {
      deep: true,
      handler() {
        this.fetchTemplates(this.search);
        this.updateSelectedFacetsFlat();
      },
    },
  },
  mounted() {
    this.updateSelectedFacetsFlat();
    this.$ld.on(this.onChangeLDFlags);
  },
  beforeUnmount() {
    this.$ld.off(this.onChangeLDFlags);
  },
  methods: {
    ...mapActions('graphicsTemplate', [
      'setElementStoreValues',
      'setTemplateTemplateId',
    ]),

    onChangeLDFlags() {
      // Kept for other flags if needed
    },

    fetchTemplates: debounce(async function doSearch(search) {
      this.loading = true;
      try {
        // Use Packs & Templates API
        const params = {
          search,
          assetType: 'graphic',
          ...this.selectedFacets,
        };

        const { results, facets } = await Packs.search(params);
        this.templates = results;
        this.availableFacets = facets || {};
        this.$emit('facets-loaded', facets || {});
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load templates:', err);
      } finally {
        this.loading = false;
      }
    }, 250),

    isDisabled(template) {
      // Check for limited elements based on dataTypes and signals
      const limitedDataTypes = ['social', 'shoppable', 'social-display'];
      const hasLimitedElements = template.dataTypes?.some((dt) => limitedDataTypes.includes(dt));

      if (hasLimitedElements) {
        return template.dataTypes.some((dataType) => {
          const elementData = this.elementMetaLookup[dataType];
          if (!elementData) {
            return false;
          }

          switch (dataType) {
            case 'social':
            case 'social-display':
            case 'shoppable': {
              const limitedKeys = elementData.limitedElement ?? [dataType];
              const count = limitedKeys.reduce(
                (sum, key) => sum + (this.getElementCounts[key] || 0),
                0,
              );
              return count >= elementData.maxElements;
            }

            default: {
              return false;
            }
          }
        });
      }

      return false;
    },

    async useTemplateClick(template) {
      await this.useTemplate(template);
      this.$emit('used', template);
    },

    async useTemplate(template) {
      // Use Packs & Templates API
      const templateData = await Packs.getTemplate(template.id);

      let elementsToAdd = templateData.settings?.elements || [];

      // Check if we already have unique display elements on the canvas
      const hasSocialData = (this.getElementCounts['social-data'] || 0) > 0;
      const hasSocialDisplay = (this.getElementCounts['social-display'] || 0) > 0;
      const hasPollDisplay = (this.getElementCounts['poll-display'] || 0) > 0;

      // Track which elements were filtered out
      const filteredOut = [];

      // Recursively filter out duplicate display elements from the template
      const filterElements = (elements) => elements
        .filter((element) => {
          // Exclude duplicate display elements
          if (element.type === 'social-data' && hasSocialData) {
            filteredOut.push('Social Graphic');
            return false;
          }
          if (element.type === 'social-display' && hasSocialDisplay) {
            filteredOut.push('Social Graphic');
            return false;
          }
          if (element.type === 'poll-display' && hasPollDisplay) {
            filteredOut.push('Poll Graphic');
            return false;
          }
          return true;
        })
        .map((element) => {
          // Recursively filter children for groups/slideshows
          if (element.elements && Array.isArray(element.elements)) {
            return {
              ...element,
              elements: filterElements(element.elements),
            };
          }
          return element;
        });

      elementsToAdd = filterElements(elementsToAdd);

      // Show warning toast if any elements were filtered out
      if (filteredOut.length > 0) {
        const uniqueFiltered = [...new Set(filteredOut)];
        const elementList = uniqueFiltered.join(' and ');
        toast.makeToast({
          heading: 'Warning',
          icon: 'warning',
          hideAfter: 5000,
          stack: false,
          text: `${elementList} element${uniqueFiltered.length > 1 ? 's were' : ' was'} excluded because you already have one on the canvas. Only one ${elementList} is allowed per graphic.`,
        });
      }

      this.setElementStoreValues({
        value: elementsToAdd,
        options: {},
      });

      // Set template_id for editing
      this.setTemplateTemplateId({ value: template.id });
    },

    checkAvailability: (hasPermission, item, link) => {
      const itemCopy = { ...item };

      // Check if template is marked as beta via signals or theme
      const isBeta = item.signals?.includes('beta') || item.theme === 'beta';

      if (isBeta) {
        // Filter out beta templates if user doesn't have permissions
        return hasPermission ? item : false;
      }

      if (!hasPermission) {
        itemCopy.upsell = true; // add upsell flag to non-beta templates
        itemCopy.upsellLink = link;
        return itemCopy;
      }

      return item;
    },

    openPreview(template) {
      this.$emit('preview', template);
    },

    formatFacetLabel(facetKey) {
      return startCase(facetKey);
    },

    capitalizeWords(str) {
      return str
        .split(/([- ])/)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join('');
    },

    isFacetSelected(facetKey, value) {
      return this.selectedFacets[facetKey]?.includes(value) || false;
    },

    removeFacet(facetKey, value) {
      this.$emit('remove-facet', { facetKey, value });
    },

    updateSelectedFacetsFlat() {
      this.selectedFacetsFlat = Object.entries(this.selectedFacets).flatMap(
        ([facet, values]) => values.map((value) => ({ facet, value })),
      );
    },
  },
};
</script>

<style scoped lang="scss">
.template-menu {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__selected-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding-bottom: 8px;
    border-bottom: 1px solid #dfeaef;
  }

  &__loading,
  &__empty {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: #666;
  }

  &__list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #f0fdff;
  border: 1px solid #00b4cc;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: #00b4cc;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #00b4cc;
    color: #ffffff;
  }

  .fa-times {
    font-size: 9px;
  }
}

.template-item {
  position: relative;
}

/* Main card (image + title) */
.template-card-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  border-radius: 6px;
  border: 1px solid transparent;
  background: #f1f1f1;
  padding: 6px;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  transition:
    transform 0.16s ease-out,
    box-shadow 0.16s ease-out,
    border-color 0.16s ease-out,
    background-color 0.16s ease-out;

  &:hover,
  &:focus {
    transform: translateY(-1px);
    border-color: #00b4cc;
    background-color: #f0fdff;
    outline: none;
    box-shadow:
      0 10px 18px rgba(15, 23, 42, 0.18),
      0 4px 6px rgba(15, 23, 42, 0.14);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    transform: none;
    box-shadow:
      0 3px 5px rgba(0, 0, 0, 0.02),
      0 0 2px rgba(0, 0, 0, 0.05),
      0 1px 4px rgba(0, 0, 0, 0.08);
  }
}

.template-card-button--upsell {
  position: relative;
}

.template-card__thumb {
  width: 100%;
  border-radius: 6px;
  overflow: hidden;
  aspect-ratio: 16 / 9;
}

.template-card__image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.template-card__title {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.template-card__bolt {
  margin-left: 4px;
  font-size: 12px;
  color: #fbbf24;
}

.template-card__preview-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 999px;
  border: 1px solid #dfeaef;
  padding: 2px 5px;
  line-height: 1;
  transition:
    background-color 0.16s ease-out,
    border-color 0.16s ease-out,
    box-shadow 0.16s ease-out;

  .fa-magnifying-glass {
    font-size: 12px;
  }

  &:hover,
  &:focus {
    background: #ffffff;
    border-color: #00b4cc;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.15);
    outline: none;
  }
}
</style>
